import { Badge, Button, Dropdown, Form } from 'react-bootstrap';

export interface MultiSelectOption<T extends string | number = string | number> {
  id: T;
  label: string;
}

/** Multi-select dropdown: checkbox items, selected-count badge, scrollable menu. */
export function MultiSelectDropdown<T extends string | number>({
  label,
  icon,
  options,
  selectedIds,
  onChange,
  isLoading,
  emptyMessage = 'No options available',
  actionsBox = false,
}: {
  label: string;
  icon: string;
  options: MultiSelectOption<T>[];
  selectedIds: T[];
  onChange: (ids: T[]) => void;
  isLoading?: boolean;
  emptyMessage?: string;
  /** Shows "Select All | Deselect All" buttons pinned above the option list. */
  actionsBox?: boolean;
}) {
  const toggle = (id: T) => {
    onChange(selectedIds.includes(id) ? selectedIds.filter((x) => x !== id) : [...selectedIds, id]);
  };

  return (
    <Dropdown autoClose="outside">
      <Dropdown.Toggle variant="outline-secondary" size="sm" className="w-100 text-start">
        <i className={`bi ${icon} me-1`} aria-hidden="true" />
        {label}
        {selectedIds.length > 0 && (
          <Badge bg="primary" pill className="ms-2">
            {selectedIds.length}
          </Badge>
        )}
      </Dropdown.Toggle>
      <Dropdown.Menu className="w-100 px-2">
        {actionsBox && !isLoading && options.length > 0 && (
          <div className="d-flex border-bottom pb-1 mb-1">
            <Button
              variant="link"
              size="sm"
              className="flex-fill text-decoration-none py-0 border-end"
              onClick={() => onChange(options.map((o) => o.id))}
            >
              Select All
            </Button>
            <Button
              variant="link"
              size="sm"
              className="flex-fill text-decoration-none py-0"
              onClick={() => onChange([])}
            >
              Deselect All
            </Button>
          </div>
        )}
        <div style={{ maxHeight: 300, overflowY: 'auto' }}>
          {isLoading && <div className="text-muted small px-2 py-1">Loading...</div>}
          {!isLoading && options.length === 0 && (
            <div className="text-muted small px-2 py-1">{emptyMessage}</div>
          )}
          {options.map((opt) => (
            <Form.Check
              key={opt.id}
              type="checkbox"
              id={`${label}-${opt.id}`.replace(/\s+/g, '-')}
              label={opt.label}
              checked={selectedIds.includes(opt.id)}
              onChange={() => toggle(opt.id)}
              className="px-2 py-1 ms-3"
            />
          ))}
        </div>
      </Dropdown.Menu>
    </Dropdown>
  );
}
